import { z } from "zod";
import { ExternalMappingSchema } from "./externalMappings";

export const OPERATION_SOURCE_TYPE_VALUES = ["INSIDE", "OUTSIDE"] as const;
export const OperationSourceTypeEnum = z.enum(OPERATION_SOURCE_TYPE_VALUES);
export type OperationSourceType = z.infer<typeof OperationSourceTypeEnum>;
export const OPERATION_SOURCE_TYPE = OperationSourceTypeEnum.enum;

export const UNIT_COST_CALCULATION_VALUES = ["TIME", "PER_UNIT"] as const;
export const UnitCostCalculationEnum = z.enum(UNIT_COST_CALCULATION_VALUES);
export type UnitCostCalculation = z.infer<typeof UnitCostCalculationEnum>;
export const UNIT_COST_CALCULATION = UnitCostCalculationEnum.enum;

const OperationNameSchema = z
  .string()
  .trim()
  .min(1, "Operation name is required");

// Core Operation schema
export const OperationSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  cost: z.string(),
  burdenRate: z.string().nullable().optional(),
  setupRate: z.string().nullable().optional(),
  operationSourceType: OperationSourceTypeEnum.optional().default("INSIDE"),
  unitCostCalculation: UnitCostCalculationEnum.optional().default("TIME"),
  organizationId: z.string(),
  externalMappings: z.array(ExternalMappingSchema).optional(),
  archivedAt: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type Operation = z.infer<typeof OperationSchema>;

// API Request schemas
export const CreateOperationParamsSchema = z.object({
  name: OperationNameSchema,
  description: z.string().nullable().optional(),
  cost: z.string(),
  burdenRate: z.string().nullable().optional(),
  setupRate: z.string().nullable().optional(),
  operationSourceType: OperationSourceTypeEnum.optional(),
  unitCostCalculation: UnitCostCalculationEnum.optional(),
});

export type CreateOperationParams = z.infer<typeof CreateOperationParamsSchema>;

export const UpdateOperationParamsSchema = z.object({
  name: OperationNameSchema.optional(),
  description: z.string().nullable().optional(),
  cost: z.union([z.string(), z.number()]).optional(),
  burdenRate: z.union([z.string(), z.number()]).nullable().optional(),
  setupRate: z.union([z.string(), z.number()]).nullable().optional(),
  operationSourceType: OperationSourceTypeEnum.optional(),
  unitCostCalculation: UnitCostCalculationEnum.optional(),
  archived: z.boolean().optional(),
});

export type UpdateOperationParams = z.infer<typeof UpdateOperationParamsSchema>;

// Filter for searching operations
export const OperationFilterSchema = z.object({
  search: z.string().optional(),
  limit: z.number().int().positive().optional(),
  offset: z.number().int().nonnegative().optional(),
  includeArchived: z.boolean().optional(),
});

export type OperationFilter = z.infer<typeof OperationFilterSchema>;

// API Response schemas
export const CreateOperationResponseSchema = z.object({
  operation: OperationSchema,
});

export type CreateOperationResponse = z.infer<
  typeof CreateOperationResponseSchema
>;

export const GetOperationResponseSchema = z.object({
  operation: OperationSchema,
});

export type GetOperationResponse = z.infer<typeof GetOperationResponseSchema>;

export const UpdateOperationResponseSchema = z.object({
  operation: OperationSchema,
});

export type UpdateOperationResponse = z.infer<
  typeof UpdateOperationResponseSchema
>;

export const ListOperationsResponseSchema = z.object({
  operations: z.array(OperationSchema),
  total: z.number(),
});

export type ListOperationsResponse = z.infer<
  typeof ListOperationsResponseSchema
>;

export const DeleteOperationResponseSchema = z.object({
  success: z.boolean(),
});

export type DeleteOperationResponse = z.infer<
  typeof DeleteOperationResponseSchema
>;

// Error Response Schema
export const OperationErrorResponseSchema = z.object({
  error: z.string(),
});

export type OperationErrorResponse = z.infer<
  typeof OperationErrorResponseSchema
>;

// Path parameter schemas
export const OperationIdParamsSchema = z.object({
  id: z.string(),
});

export type OperationIdParams = z.infer<typeof OperationIdParamsSchema>;
